<template>
  <div>
    <b-form-input
      ref="input"
      type="text"
      :value="displayValue"
      :class="['text-right', css]"
      :state="state"
      :placeholder="placeholder"
      :disabled="disabled"
      @input="handleInput"
      @blur="handleBlur"
      @keypress="validateKeypress"
    />
  </div>
</template>

<script>
export default {
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    css: {
      type: [String, Array, Object],
      default: ''
    },
    state: {
      type: Boolean,
      default: null
    },
    placeholder: {
      type: String,
      default: "$0"
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      displayValue: '',
      isEditing: false
    };
  },
  mounted() {
    this.formatToDisplay(this.value);
  },
  watch: {
    value: {
      handler(newVal) {
        // Only update if not being edited
        if (!this.isEditing) {
          this.formatToDisplay(newVal);
        }
      }
    }
  },
  methods: {
    // Format a value for display
    formatToDisplay(val) {
      if (val === '' || val === null || val === undefined) {
        this.displayValue = '';
        return;
      }
      
      try {
        // Ensure val is a number
        const numVal = typeof val === 'string' ? parseFloat(val) : val;
        
        if (isNaN(numVal)) {
          this.displayValue = '';
          return;
        }
        
        // Format with $ and commas and EXACTLY 2 decimal places
        this.displayValue = '$' + numVal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      } catch (e) {
        console.error('Error formatting value:', e);
        this.displayValue = val.toString();
      }
    },
    
    // Validate keypress to prevent non-numeric input
    validateKeypress(event) {
      const keyCode = event.keyCode || event.which;
      const keyChar = String.fromCharCode(keyCode);
      
      // Allow control keys (backspace, delete, arrows, etc.)
      if (keyCode < 32) {
        return true;
      }
      
      // Allow dollar sign only at the beginning
      if (keyChar === '$') {
        // Only allow $ at the beginning
        const inputElement = this.$refs.input.$el.querySelector('input');
        if (inputElement && (
            this.displayValue.includes('$') || 
            inputElement.selectionStart > 0
        )) {
          event.preventDefault();
          return false;
        }
        return true;
      }
      
      // Allow comma for thousands separator
      if (keyChar === ',') {
        return true;
      }
      
      // Allow decimal point only once
      if (keyChar === '.') {
        if (this.displayValue.includes('.')) {
          event.preventDefault();
          return false;
        }
        return true;
      }
      
      // Only allow digits, decimal point, comma, or dollar sign
      if (!/[\d.,$]/.test(keyChar)) {
        event.preventDefault();
        return false;
      }
    },
    
    handleInput(val) {
      // Set editing flag
      this.isEditing = true;
      
      // Store the raw input in the display
      this.displayValue = val;
      
      // Try to extract a clean number value if possible
      if (!val) {
        this.$emit('input', '');
        return;
      }
      
      // Extract just digits and decimal point
      const cleaned = val.replace(/[^\d.]/g, '');
      
      // Handle case with no digits
      if (!cleaned) {
        this.$emit('input', '');
        return;
      }
      
      // Fix multiple decimal points
      let fixedValue = cleaned;
      if (cleaned.indexOf('.') !== cleaned.lastIndexOf('.')) {
        const parts = cleaned.split('.');
        fixedValue = parts[0] + '.' + parts.slice(1).join('');
      }
      
      // Try to convert to a number and emit
      const numVal = parseFloat(fixedValue);
      if (!isNaN(numVal)) {
        // Emit the value
        this.$emit('input', numVal);
      }
    },
    
    handleBlur() {
      // Reset editing flag
      this.isEditing = false;
      
      // Aggressively enforce currency format on blur
      if (!this.displayValue) {
        this.$emit('input', '');
        this.displayValue = '';
        return;
      }
      
      try {
        // Clean the value - remove everything except digits and decimal
        const cleaned = this.displayValue.replace(/[^\d.]/g, '');
        
        // Handle empty case
        if (!cleaned) {
          this.$emit('input', '');
          this.displayValue = '';
          return;
        }
        
        // Fix decimal points
        let fixedValue = cleaned;
        if (cleaned.indexOf('.') !== cleaned.lastIndexOf('.')) {
          const parts = cleaned.split('.');
          fixedValue = parts[0] + '.' + parts.slice(1).join('');
        }
        
        // Truncate to exactly 2 decimal places
        let numVal = parseFloat(fixedValue);
        
        // If there are decimal places, ensure exactly 2
        if (fixedValue.includes('.')) {
          const parts = fixedValue.split('.');
          // If we have more than 2 decimal places, truncate
          if (parts[1] && parts[1].length > 2) {
            parts[1] = parts[1].substring(0, 2);
            numVal = parseFloat(parts[0] + '.' + parts[1]);
          }
        }
        
        // Update model with truncated value
        this.$emit('input', numVal);
        
        // Force formatting
        this.formatToDisplay(numVal);
      } catch (e) {
        console.error('Error on blur:', e);
        // Fallback to formatting the current value
        this.formatToDisplay(this.value);
      }
    }
  }
};
</script>